import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { api, errMsg, money, statusClass, statusLabel, verifyClass, CASE_STATUSES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Badge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = {
  title: "", claimant: "", respondent: "", arbitrator_ids: [], arbitrator_count: 1,
  presiding_arbitrator_id: "", plan: "basic", seat: "",
  claim_amount: "", currency: "INR", description: "", proposed_case_number: "",
};

export const PLANS = [
  { value: "basic", fee: 5000, label: "Basic — INR 5,000", blurb: "INR 2,500 per side. Track case status and read orders. No e-filing and no downloads." },
  { value: "premium", fee: 7000, label: "Full access — INR 7,000", blurb: "INR 3,500 per side. All facilities, including e-filing and download of pleadings, orders and awards." },
];

const Field = ({ label, value, onChange, type = "text", required, testId, placeholder }) => (
  <div className="space-y-2">
    <Label className="label-caps">{label}</Label>
    <Input data-testid={testId} type={type} required={required} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className="rounded-none focus:ring-2 focus:ring-blue-800" />
  </div>
);

export default function Cases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [arbitrators, setArbitrators] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const isAdvocate = user?.role === "advocate";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const params = {};
      if (query) params.q = query;
      if (statusFilter !== "all") params.status = statusFilter;
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      const { data } = await api.get("/cases", { params });
      setCases(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, [query, statusFilter, range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (user?.role !== "client") api.get("/arbitrators").then(({ data }) => setArbitrators(data)).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.arbitrator_ids.length === 0) return toast.error("Select at least one arbitrator from the panel");
    if (form.arbitrator_ids.length > form.arbitrator_count)
      return toast.error(`You have selected more arbitrators than the tribunal of ${form.arbitrator_count}`);
    setBusy(true);
    try {
      const { data } = await api.post("/cases", {
        ...form,
        presiding_arbitrator_id: form.presiding_arbitrator_id || form.arbitrator_ids[0],
        claim_amount: form.claim_amount === "" ? null : Number(form.claim_amount),
      });
      toast.success(`${data.ref_id} filed — acceptance links sent to ${form.arbitrator_ids.length} arbitrator(s)`);
      setInvites(data.arbitrator_invites || []);
      setOpen(false);
      setForm(blank);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleArb = (id) =>
    setForm((f) => {
      const has = f.arbitrator_ids.includes(id);
      if (!has && f.arbitrator_ids.length >= f.arbitrator_count) {
        toast.error(`A tribunal of ${f.arbitrator_count} — deselect one or raise the number first`);
        return f;
      }
      const ids = has ? f.arbitrator_ids.filter((x) => x !== id) : [...f.arbitrator_ids, id];
      return {
        ...f,
        arbitrator_ids: ids,
        presiding_arbitrator_id: ids.includes(f.presiding_arbitrator_id) ? f.presiding_arbitrator_id : (ids[0] || ""),
      };
    });

  return (
    <div data-testid="cases-page">
      <PageHeader
        eyebrow="Registry"
        title="My Cases"
        description={
          user?.role === "arbitrator"
            ? "References pending before you. Verify newly filed matters and assign your file number."
            : user?.role === "advocate"
            ? "References in which you appear. File a new reference and tag the opposite counsel and your client."
            : "References tagged to you by your advocate. Tribunal contact details are confidential."
        }
        actions={
          isAdvocate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-case-button"
                  className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-6">
                  <Plus className="h-4 w-4 mr-2" /> File a reference
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="new-case-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">File a new reference</DialogTitle>
                  <DialogDescription className="text-sm text-muted-foreground">
                    The arbitrator will verify these particulars and assign the file number.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-4">
                  <div className="sm:col-span-2">
                    <Field label="Cause title" required value={form.title} onChange={(v) => set("title", v)}
                      testId="case-title-input" placeholder="M/s Orion Infra vs. NHAI" />
                  </div>
                  <Field label="Claimant" required value={form.claimant} onChange={(v) => set("claimant", v)} testId="case-claimant-input" />
                  <Field label="Respondent" required value={form.respondent} onChange={(v) => set("respondent", v)} testId="case-respondent-input" />
                  <div className="space-y-2">
                    <Label className="label-caps">Number of arbitrators</Label>
                    <Select value={String(form.arbitrator_count)}
                      onValueChange={(v) => set("arbitrator_count", Number(v))}>
                      <SelectTrigger className="rounded-none" data-testid="arbitrator-count-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                          <SelectItem key={n} value={String(n)} data-testid={`arbitrator-count-${n}`}>
                            {n === 1 ? "Sole arbitrator" : `${n} arbitrators`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Field label="Proposed file no. (optional)" value={form.proposed_case_number}
                    onChange={(v) => set("proposed_case_number", v)} testId="case-proposed-number-input" />
                  <div className="sm:col-span-2 space-y-2">
                    <Label className="label-caps">
                      Choose from the panel ({form.arbitrator_ids.length} of {form.arbitrator_count} selected)
                    </Label>
                    <div className="border border-border divide-y divide-border max-h-56 overflow-y-auto" data-testid="arbitrator-picker">
                      {arbitrators.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-muted-foreground">No arbitrators are registered yet.</p>
                      ) : arbitrators.map((a) => {
                        const picked = form.arbitrator_ids.includes(a.id);
                        return (
                          <div key={a.id} className={`px-4 py-3 flex items-center justify-between gap-4 transition-colors duration-200 ${picked ? "bg-blue-50 dark:bg-blue-950/40" : ""}`}>
                            <button type="button" data-testid={`arbitrator-option-${a.email}`} onClick={() => toggleArb(a.id)}
                              className="text-left min-w-0 flex-1">
                              <p className="text-sm font-medium">{a.name}</p>
                              <p className="text-xs text-muted-foreground font-mono truncate">
                                {[a.chamber, a.phone, a.email].filter(Boolean).join(" · ")}
                              </p>
                            </button>
                            {picked && (
                              <button type="button" data-testid={`presiding-option-${a.email}`}
                                onClick={() => set("presiding_arbitrator_id", a.id)}
                                className={`text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors duration-200 ${
                                  (form.presiding_arbitrator_id || form.arbitrator_ids[0]) === a.id
                                    ? "border-blue-900 bg-blue-900 text-white"
                                    : "border-border hover:bg-accent"
                                }`}>
                                Presiding
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Each selected arbitrator goes on record straight away and is sent an email with an
                      acceptance link. If one declines, they come off the tribunal and you may request another.
                    </p>
                  </div>
                  <Field label="Seat / venue" value={form.seat} onChange={(v) => set("seat", v)} testId="case-seat-input" placeholder="Bengaluru" />
                  <Field label="Claim amount" type="number" value={form.claim_amount} onChange={(v) => set("claim_amount", v)} testId="case-amount-input" />
                  <div className="sm:col-span-2 space-y-2">
                    <Label className="label-caps">Case fee plan (shared equally by both sides)</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="plan-picker">
                      {PLANS.map((p) => (
                        <button key={p.value} type="button" data-testid={`plan-option-${p.value}`}
                          onClick={() => set("plan", p.value)}
                          className={`border p-4 text-left transition-colors duration-200 ${
                            form.plan === p.value
                              ? "border-blue-900 bg-blue-50 dark:bg-blue-950/40"
                              : "border-border hover:bg-accent"
                          }`}>
                          <p className="text-sm font-medium">{p.label}</p>
                          <p className="text-xs text-muted-foreground mt-1.5">{p.blurb}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2 space-y-2">
                    <Label className="label-caps">Brief facts</Label>
                    <Textarea data-testid="case-description-input" rows={3} className="rounded-none"
                      value={form.description} onChange={(e) => set("description", e.target.value)} />
                  </div>
                  <DialogFooter className="sm:col-span-2">
                    <Button type="submit" disabled={busy} data-testid="submit-case-button"
                      className="rounded-none bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs h-11 px-8">
                      {busy ? "Filing…" : "File reference"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {invites.length > 0 && (
        <div className="mb-6 border border-blue-900/40 bg-blue-50 dark:bg-blue-950/30 p-5" data-testid="filed-invites">
          <div className="flex items-start justify-between gap-4">
            <p className="label-caps">Acceptance links sent — share on WhatsApp too</p>
            <button onClick={() => setInvites([])} className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" data-testid="filed-invites-dismiss">Dismiss</button>
          </div>
          {invites.map((i) => (
            <div key={i.url} className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`filed-invite-${i.arbitrator_name}`}>
              <div className="min-w-0"><p className="font-medium">{i.arbitrator_name}</p><p className="font-mono text-xs break-all text-muted-foreground">{i.url}</p></div>
              <a href={i.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid={`filed-invite-whatsapp-${i.arbitrator_name}`}
                className="inline-flex items-center h-9 px-3 bg-green-700 hover:bg-green-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">WhatsApp</a>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="case-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by file no., cause or party" className="rounded-none pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none w-full sm:w-56" data-testid="case-status-filter"><SelectValue placeholder="All stages" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {CASE_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} data-testid="case-date-from" title="Filed from"
          className="h-10 px-3 border border-border bg-background text-sm rounded-none" />
        <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} data-testid="case-date-to" title="Filed to"
          className="h-10 px-3 border border-border bg-background text-sm rounded-none" />
      </div>

      {cases.length === 0 ? (
        <EmptyState testId="cases-empty" title="No references yet"
          hint={isAdvocate ? "File your first reference and select the arbitrator." : "You will see references here once you are tagged."} />
      ) : (
        <div className="border border-border bg-card overflow-x-auto" data-testid="cases-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-muted/60">
                {["File no.", "Cause", "Claimant", "Respondent", "Stage", "Verification",
                  ...(user?.role === "arbitrator" ? ["Registry"] : ["Fee status"]), "Claim"].map((h) => (
                  <th key={h} className="p-4 text-left label-caps font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => (
                <tr key={c.id} data-testid={`case-row-${c.ref_id.replace(/\//g, "-")}`}
                  onClick={() => navigate(`/cases/${c.id}`)}
                  className="border-b border-border last:border-0 cursor-pointer hover:bg-accent transition-colors duration-200">
                  <td className="p-4 font-mono text-xs">
                    {c.case_number || c.ref_id}
                    {c.case_number && <span className="block text-muted-foreground mt-1">{c.ref_id}</span>}
                  </td>
                  <td className="p-4 font-medium">{c.title}</td>
                  <td className="p-4 text-muted-foreground">{c.claimant}</td>
                  <td className="p-4 text-muted-foreground">{c.respondent}</td>
                  <td className="p-4">
                    <Badge className={statusClass(c.status)}>{statusLabel(c.status)}</Badge>
                    {c.plan && (
                      <Badge className={`ml-2 ${c.plan === "premium" ? "bg-green-50 text-green-900" : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"}`}>
                        {c.plan === "premium" ? "full" : "basic"}
                      </Badge>
                    )}
                  </td>
                  <td className="p-4"><Badge className={verifyClass(c.verification_status)}>{c.verification_status}</Badge></td>
                  <td className="p-4">
                    <Badge testId={`case-fee-status-${c.id}`}
                      className={c.fully_activated ? "bg-green-50 text-green-900" : "bg-amber-50 text-amber-900"}>
                      {c.fully_activated
                        ? (user?.role === "arbitrator" ? "formalities complete" : "both halves paid")
                        : (user?.role === "arbitrator" ? "formalities pending" : "awaiting payment")}
                    </Badge>
                    {!c.fully_activated && user?.role !== "arbitrator" && (
                      <span className="block text-[11px] text-muted-foreground mt-1" data-testid={`case-fee-detail-${c.id}`}>
                        claimant {c.claimant_paid ? "paid" : "pending"} · respondent {c.respondent_paid ? "paid" : "pending"}
                      </span>
                    )}
                  </td>
                  <td className="p-4 font-mono text-xs">{money(c.claim_amount, c.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
